/*
 * smart-video.js — one shared, idempotent controller for [data-smart-video] tiles.
 *
 * Behaviour (see snippets/smart-video.liquid):
 *  - Eligibility gate FIRST: prefers-reduced-motion / Save-Data / 2g => nothing loads, poster only.
 *  - Viewport-gated: only the winning tile(s) in a group load & play. Budget = 1 on mobile,
 *    all tiles on desktop (>=990px). Ties break by intersection ratio then DOM order.
 *  - Mobile cap is SITE-WIDE, not per-group: each section (hero, grid, testimonial banner)
 *    still separately picks its own best-in-view tile, but enforceMobileCap() then keeps
 *    only the single highest-ratio tile across ALL sections active on mobile, deactivating
 *    the rest. Without this, scrolling from one video section into the next produces a
 *    window where both are decoding at once (confirmed on-device: simultaneous ~150-580ms
 *    freezes in both). Desktop is unaffected (multiple tiles per group is intended there).
 *  - Backend, exactly one, chosen at activation with NO <source> in the DOM:
 *      native HLS (Safari/iOS)  -> assign the .m3u8 to the element,
 *      else hls.js (Chrome/Android, lazy-loaded once) -> adaptive ladder, capped to tile size,
 *      else progressive MP4     -> last resort only when neither is available.
 *  - Reveal on a real presented frame (requestVideoFrameCallback) or playing+readyState,
 *    with an 8s deadline that STOPS loading and keeps the poster (never reveals on timeout).
 *  - Deactivate => hls.destroy()/stopLoad() (pause alone does not stop buffering).
 *  - Bounded recovery: media error -> recoverMediaError once; network/other fatal -> poster.
 *    A slow network never escalates back to the heavy MP4.
 */
(function () {
  'use strict';
  if (window.__ptSmartVideo) return;            // idempotent across duplicate script tags
  window.__ptSmartVideo = true;

  var CFG = window.PTSmartVideo || {};
  var HLS_URL = CFG.hlsUrl || null;
  var REVEAL_DEADLINE = 8000;

  // Diagnostic overlay, OFF by default. Enabled only when the page URL carries
  // ?svdebug=1 (e.g. the draft preview link). When off, DEBUG is false and every
  // debug branch below is skipped -> zero behaviour change for real visitors.
  var DEBUG = false;
  try { DEBUG = /[?&]svdebug=1(&|$)/.test(location.search); } catch (e) {}

  var mqDesktop = window.matchMedia ? window.matchMedia('(min-width: 990px)') : { matches: false };
  var mqReduce  = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function saveData() {
    var c = navigator.connection || navigator.webkitConnection;
    if (!c) return false;                        // unknown != slow
    if (c.saveData) return true;
    return /(^|\b)(slow-2g|2g)$/.test(c.effectiveType || '');
  }
  function eligible() {
    return !mqReduce.matches && !saveData() && ('IntersectionObserver' in window);
  }

  // ---- native HLS detection ----
  // Only the Apple MIME counts as "native" (Safari/iOS, where MSE/hls.js is unavailable on
  // iPhone so native HLS is mandatory). We deliberately IGNORE the legacy x-mpegURL "maybe"
  // that some old/OEM Android Chrome reports with a flaky native HLS impl — those have MSE,
  // so hls.js gives them the reliable adaptive path instead.
  var _native = null;
  function nativeHls() {
    if (_native !== null) return _native;
    var v = document.createElement('video');
    _native = /maybe|probably/.test(v.canPlayType('application/vnd.apple.mpegurl'));
    return _native;
  }

  // ---- lazy hls.js loader (single shared promise) ----
  var _hlsP = null;
  function loadHls() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (_hlsP) return _hlsP;
    _hlsP = new Promise(function (res, rej) {
      if (!HLS_URL) { rej(new Error('no hls url')); return; }
      var s = document.createElement('script');
      s.src = HLS_URL; s.async = true;
      s.onload = function () { window.Hls ? res(window.Hls) : rej(new Error('hls not defined')); };
      s.onerror = function () { rej(new Error('hls script error')); };
      document.head.appendChild(s);
    });
    return _hlsP;
  }

  // ---- debug overlay (only constructed when DEBUG; null otherwise) ----
  // Shows, per active tile: current HLS level (Android/hls.js only), a switch
  // counter, a freeze counter, and a scrolling list of the last events with a
  // wall-clock stamp and the video's currentTime. The freeze detector is backend
  // agnostic (watches the <video> element), so it also captures iPhone native-HLS
  // hitches where the HLS level itself is not observable from JS.
  var Dbg = (function () {
    if (!DEBUG) return null;
    var box = null, rows = [], MAX = 14, head = Object.create(null);
    function ensure() {
      if (box) return;
      box = document.createElement('div');
      box.setAttribute('style', [
        'position:fixed', 'left:6px', 'bottom:6px', 'z-index:2147483647',
        'max-width:82vw', 'max-height:48vh', 'overflow:hidden',
        'background:rgba(0,0,0,.82)', 'color:#39ff14', 'font:11px/1.35 monospace',
        'padding:6px 8px', 'border-radius:6px', 'pointer-events:none',
        'white-space:pre', 'text-shadow:0 0 2px #000'
      ].join(';'));
      (document.body || document.documentElement).appendChild(box);
    }
    function stamp() {
      var d = new Date(), p2 = function (n) { return ('0' + n).slice(-2); };
      return p2(d.getMinutes()) + ':' + p2(d.getSeconds()) + '.' + ('00' + d.getMilliseconds()).slice(-3);
    }
    function render() {
      ensure();
      var top = Object.keys(head).map(function (k) {
        var h = head[k], a = h.anom || {};
        var an = Object.keys(a).map(function (kk) { return a[kk]; }).filter(Boolean).join(' ');
        return k + ' ' + (h.lvl || '?') + ' sw' + (h.sw || 0) + ' fz' + (h.fz || 0) + ' lp' + (h.lp || 0)
          + (an ? '  ' + an : '');
      }).join('\n');
      box.textContent = top + (top ? '\n' : '') + rows.join('\n');
    }
    return {
      lvl:  function (label, txt) { (head[label] = head[label] || {}).lvl = txt; render(); },
      bump: function (label, key) { var h = head[label] = head[label] || {}; h[key] = (h[key] || 0) + 1; },
      // Sticky per-category evidence (hitch / seam / live) so a recurring seam never
      // overwrites the important mid-clip hitch, and the current-freeze line is separate.
      // Pass txt='' to clear a slot. Stays in the top summary so it survives in a screenshot.
      anomaly: function (label, key, txt) { var h = head[label] = head[label] || {}; (h.anom = h.anom || {})[key] = txt; render(); },
      log:  function (label, msg) {
        rows.unshift(stamp() + ' ' + label + ' ' + msg);
        if (rows.length > MAX) rows.length = MAX;
        render();
      }
    };
  })();

  // ---- group registry ----
  var groups = Object.create(null);
  function getGroup(name) {
    return groups[name] || (groups[name] = { name: name, tiles: [] });
  }
  // Global tile counter for debug labels ('T0','T1',...). Must be unique SITE-WIDE,
  // not per-group: two different sections' first tile would otherwise both log as
  // "T0" and their event histories would be indistinguishable in the Dbg overlay.
  var globalTileCount = 0;

  function Tile(el) {
    this.el = el;
    this.video = el.querySelector('[data-smart-video-el]');
    this.group = el.getAttribute('data-group') || 'default';
    this.order = 0;
    this.ratio = 0;
    this.active = false;
    this.revealed = false;
    this.hls = null;
    this.token = 0;                              // invalidates stale async work
    this.deadline = null;
    // Ping-pong (opt-in via data-pingpong on the wrapper + a second <video>).
    // A crossfading double-buffer that avoids the iOS native-loop decode freeze;
    // null/false for every normal tile so their path is completely unchanged.
    this.videoB = el.querySelector('[data-smart-video-el-b]');
    this.pingpong = !!(this.videoB && el.getAttribute('data-pingpong') === '1');
    this.front = null;                           // currently visible clip
    this.back = null;                            // hidden clip, prepped for next swap
    this._swapping = false;
    this._ppWired = false;
    this._ppTimer = null;                        // pending recycle timer, cleared on deactivate
  }
  Tile.prototype.activate = function () {
    if (this.active || !this.video) return;
    this.active = true;
    var tok = ++this.token;
    var self = this, v = this.video;
    v.muted = true; v.defaultMuted = true;
    var m3u8 = v.getAttribute('data-hls');
    var mp4  = v.getAttribute('data-mp4');
    // Separate mobile file: a phone (<990px) fetches data-mp4-mobile instead of the
    // heavy desktop cut. Chosen once at activation; feeds both the ping-pong and the
    // plain-MP4 paths below. Absent attribute (e.g. Three Grid) => desktop mp4 unchanged.
    var mp4m = v.getAttribute('data-mp4-mobile');
    if (mp4m && !mqDesktop.matches) mp4 = mp4m;

    // Record the chosen backend for the debug overlay (label only, no behaviour change).
    // The old overlay printed the DEVICE's native-HLS capability, which read "nativeHLS"
    // even on these MP4-only tiles and looked like the fix had regressed; this prints what
    // actually plays: native-hls / hls.js / mp4.
    if (m3u8 && nativeHls()) this._backend = 'native-hls';
    else if (m3u8)          this._backend = 'hls.js';
    else                    this._backend = 'mp4';

    this.armReveal(tok);
    this.armDeadline(tok);
    this.armDebug(tok);                            // no-op unless ?svdebug=1

    // Ping-pong tiles are MP4-only (the snippet drops data-hls when data-pingpong
    // is set), so this fires before the HLS branches and takes over the loop.
    if (this.pingpong && mp4) { this.activatePingPong(tok, mp4); return; }

    if (nativeHls() && m3u8) { this.playSrc(m3u8); return; }

    if (m3u8) {
      loadHls().then(function (Hls) {
        if (tok !== self.token) return;          // deactivated while loading
        if (!Hls.isSupported()) { mp4 ? self.playSrc(mp4) : self.hold(); return; }
        var hls = new Hls({ capLevelToPlayerSize: true, startLevel: -1, maxBufferLength: 12 });
        self.hls = hls;
        hls.on(Hls.Events.ERROR, function (_e, data) {
          if (!data || !data.fatal) return;      // non-fatal: hls.js self-heals
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            try { hls.recoverMediaError(); } catch (e) { self.hold(); }
          } else {
            self.hold();                          // network/other fatal -> poster, NOT the heavy MP4
          }
        });
        hls.on(Hls.Events.MANIFEST_PARSED, function () { if (tok === self.token) self.tryPlay(); });
        if (Dbg) {
          hls.on(Hls.Events.LEVEL_SWITCHED, function (_e, d) {
            if (tok !== self.token) return;
            try {
              var L = hls.levels && hls.levels[d.level];
              var txt = L ? (L.width + 'x' + L.height) : ('#' + d.level);
              var prev = self._dbgLvl || '?';
              self._dbgLvl = txt;
              Dbg.bump('T' + self.order, 'sw');
              Dbg.lvl('T' + self.order, txt);
              Dbg.log('T' + self.order, 'SWITCH ' + prev + '->' + txt + ' @t' + v.currentTime.toFixed(2));
            } catch (e) {}
          });
        }
        hls.loadSource(m3u8);
        hls.attachMedia(v);
      }).catch(function () {
        if (tok !== self.token) return;
        mp4 ? self.playSrc(mp4) : self.hold();
      });
      return;
    }
    if (mp4) this.playSrc(mp4);
  };
  Tile.prototype.playSrc = function (src) {
    var v = this.video;
    // NOTE: keep the element's shipped preload="none" here. Forcing preload="auto" on
    // activation was tested 8 Sep and REGRESSED Android (mid-clip frame-skips, jump133ms,
    // seam grew to ~103ms) without helping iPhone — reverted. The real symptom is the fixed
    // 2.5Mbps 1080p clip not sustaining realtime on mobile, which is fixed by playing from a
    // fully-downloaded local copy (buffer-before-play), not by a preload hint.
    try { v.src = src; v.load(); } catch (e) {}
    this.tryPlay();
  };
  Tile.prototype.tryPlay = function () {
    var p = this.video.play();
    if (p && p.catch) p.catch(function () { /* autoplay blocked / interrupted -> poster holds */ });
  };
  function playEl(el) { var p = el.play(); if (p && p.catch) p.catch(function () {}); }
  // Call cb once `el` has actually PRESENTED a frame (rVFC), or is demonstrably
  // playing (playing/timeupdate with readyState>=2), or a safety cap elapses so cb is
  // never left un-called. The caller re-checks readyState in cb and only reveals a
  // decoded element, so a cap-fire on a still-undecoded clip does NOT force a swap.
  // Self-cleans all listeners on the first of those.
  function oncePresented(el, cb, capMs) {
    var done = false, rvfcId = null, to = null;
    function cleanup() {
      if (to) { clearTimeout(to); to = null; }
      if (rvfcId != null && el.cancelVideoFrameCallback) { try { el.cancelVideoFrameCallback(rvfcId); } catch (e) {} }
      el.removeEventListener('playing', onP);
      el.removeEventListener('timeupdate', onP);
    }
    function fire() { if (done) return; done = true; cleanup(); cb(); }
    function onP() { if (el.readyState >= 2 && el.currentTime > 0) fire(); }
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
      try { rvfcId = el.requestVideoFrameCallback(function () { fire(); }); } catch (e) {}
    }
    el.addEventListener('playing', onP);
    el.addEventListener('timeupdate', onP);
    to = setTimeout(fire, capMs || 600);
  }
  // ---- ping-pong double-buffer (opt-in) ----
  // Two MP4 elements, neither with `loop`. The front plays 0->end; ~LEAD seconds
  // before it ends we start the back from 0 and crossfade it in (CSS .is-front),
  // then reset+pause the outgoing clip OFF-SCREEN. So the element the user is
  // watching never seeks back to 0, which is what triggers the iOS native-loop
  // decode re-init (~400ms freeze at every wrap, with the buffer already full).
  Tile.prototype.activatePingPong = function (tok, mp4) {
    var self = this, A = this.video, B = this.videoB;
    if (!A || !B) { if (mp4) this.playSrc(mp4); return; }   // safety: single-element fallback
    A.muted = B.muted = true; A.defaultMuted = B.defaultMuted = true;
    this.front = A; this.back = B; this._swapping = false;
    A.classList.add('is-front');                 // initial front (still hidden until is-ready)
    B.classList.remove('is-front');

    // Swap listeners wired ONCE per tile (bodies gate on active + pingpong so a
    // deactivated tile never swaps). The LEAD timeupdate is primary; `ended` is a
    // safety net if a coarse timeupdate steps past the LEAD point.
    if (!this._ppWired) {
      this._ppWired = true;
      var LEAD = 0.45;
      var onTU = function () {
        if (!self.active || !self.pingpong || self._swapping) return;
        var f = self.front; if (!f) return;
        var d = f.duration;
        if (isFinite(d) && d > 0 && f.currentTime >= d - LEAD) self.ppSwap();
      };
      var onEnd = function () {
        if (!self.active || !self.pingpong || self._swapping) return;
        self.ppSwap();
      };
      A.addEventListener('timeupdate', onTU); B.addEventListener('timeupdate', onTU);
      A.addEventListener('ended', onEnd);       B.addEventListener('ended', onEnd);
    }

    try { A.src = mp4; A.load(); } catch (e) {}
    try { B.src = mp4; B.load(); } catch (e) {}  // prep the buffer partner too
    playEl(A);
  };
  Tile.prototype.ppSwap = function () {
    var self = this;
    if (this._swapping || !this.active) return;
    var f = this.front, b = this.back;
    if (!f || !b) return;
    var tok = this.token;                         // invalidates on deactivate (token++)
    this._swapping = true;
    try { b.currentTime = 0; } catch (e) {}
    playEl(b);
    // Reveal the incoming clip ONLY once it has actually presented a frame, so a
    // delayed/rejected play() never fades out the outgoing clip while the incoming
    // one is still blank/frozen. If the cap elapses with the incoming still not
    // decoded, we keep the outgoing frame (see the readyState guard in cb).
    oncePresented(b, function () {
      // Stale (a deactivate/reactivation happened): bail WITHOUT touching _swapping,
      // else we'd clear a newer swap's guard. deactivate() already reset the flag.
      if (tok !== self.token || !self.active) return;
      // Cap elapsed with the incoming still not decoded: keep the outgoing frame
      // visible (a known-good still beats revealing a blank/black element) and
      // release the guard so a later trigger can retry.
      if (b.readyState < 2) { self._swapping = false; return; }
      b.classList.add('is-front');               // crossfade in (CSS opacity 0.4s)
      f.classList.remove('is-front');            // crossfade out
      if (Dbg) { try { Dbg.bump('T' + self.order, 'lp'); Dbg.log('T' + self.order, 'PPSWAP @t' + f.currentTime.toFixed(2)); } catch (e) {} }
      // After the fade, recycle the outgoing clip: reset to 0 and pause while it is
      // hidden, so its decode re-init happens off-screen, never on the visible tile.
      self._ppTimer = setTimeout(function () {
        self._ppTimer = null;
        if (tok !== self.token || !self.active) return;   // stale: a deactivate/reactivate happened
        try { f.pause(); f.currentTime = 0; } catch (e) {}
        if (Dbg && f === self.video) self._dbgResync = true;   // A paused; re-baseline on its resume
        self.front = b; self.back = f;           // roles swap
        self._swapping = false;
      }, 450);
    }, 600);
  };
  Tile.prototype.armReveal = function (tok) {
    var self = this, v = this.video;
    function reveal() {
      if (tok !== self.token || self.revealed) return;
      self.revealed = true;
      self.el.classList.add('is-ready');
      clearTimeout(self.deadline);
    }
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
      v.requestVideoFrameCallback(function () { reveal(); });
    }
    v.addEventListener('playing', function () { if (v.readyState >= 2) reveal(); });
    v.addEventListener('timeupdate', function () { if (v.currentTime > 0 && v.readyState >= 2) reveal(); });
    // Reveal is one-way: once a real frame has shown we keep is-ready. A routine loop
    // restart fires `waiting` and briefly drops readyState 4->1; we do NOT hide the
    // video for that (the old hide-on-stall crossfaded to the poster every ~8s seam).
  };
  Tile.prototype.armDeadline = function (tok) {
    var self = this;
    this.deadline = setTimeout(function () {
      if (tok !== self.token || self.revealed) return;
      self.hold();                                // stop loading, keep poster; do NOT reveal
    }, REVEAL_DEADLINE);
  };
  // Instrument this activation (DEBUG only). The whole body is wrapped so a
  // diagnostic bug can NEVER break playback setup.
  //
  // Primary detector = requestVideoFrameCallback (rVFC): it fires once per frame
  // actually PRESENTED to the screen, and reports that frame's mediaTime. So it
  // measures the visible cadence directly. A gap between presented frames >=
  // FRAME_GAP ms is the "freeze-then-jump" the user sees -- and it is caught even
  // when currentTime keeps advancing during the stall (the failure mode a
  // currentTime-watcher is blind to). rVFC works on BOTH Android hls.js/MSE and
  // iPhone native HLS, so one detector covers both phones.
  //  - mediaTime jumping BACKWARD to ~0 = the loop seam (wrap). Logged as LOOP so a
  //    hitch at the seam can be told apart from a mid-clip hitch.
  //  - the wall gap AT the wrap tells us if the seam itself froze (intrinsic ~120ms).
  // Fallback (very old devices without rVFC) = a clock-stall watcher; clearly
  // labelled CLOCKSTALL so the two signals are never confused.
  Tile.prototype.armDebug = function (tok) {
    if (!Dbg) return;
    try {
      var self = this, v = this.video, label = 'T' + this.order;
      Dbg.log(label, 'activate rs' + v.readyState + ' ' + (self._backend || '?'));

      // Media-event listeners: attach ONCE per tile (not once per activation, which
      // leaked 6 handlers every viewport pass) and log only while active.
      if (!this._dbgWired) {
        this._dbgWired = true;
        ['waiting', 'stalled', 'playing', 'seeking', 'ended', 'error'].forEach(function (ev) {
          v.addEventListener(ev, function () {
            if (!self.active) return;
            Dbg.log('T' + self.order, ev + ' rs' + v.readyState + ' @t' + v.currentTime.toFixed(2));
          });
        });
      }

      var FRAME_GAP = 90;                          // ms between presented frames = visible hitch
      var haveRVFC = 'requestVideoFrameCallback' in HTMLVideoElement.prototype;
      if (haveRVFC) {
        // lastWall = wall-clock of the last frame actually painted; init to activation
        // time so a video that never presents a first frame still trips the watchdog.
        var lastWall = performance.now(), lastMedia = -1, seen = false, liveOn = false;
        var onFrame = function (nowW, meta) {
          if (tok !== self.token) return;          // deactivated -> stop this chain
          try {
            var mediaT = (meta && typeof meta.mediaTime === 'number') ? meta.mediaTime : v.currentTime;
            // Ping-pong recycles clip A off-screen (pause + reset to 0) while B is
            // front. When A resumes as front its first frame is ~8s of wall-clock
            // after its last, which would log a bogus seam. Re-baseline instead.
            if (self._dbgResync) { self._dbgResync = false; seen = false; lastWall = nowW; lastMedia = mediaT; }
            var wallGap = nowW - lastWall;
            var mediaGap = mediaT - lastMedia;
            if (seen) {
              if (mediaT < 0.5 && mediaGap < -0.5) {           // wrapped near start = loop seam
                Dbg.bump(label, 'lp');
                Dbg.log(label, 'LOOP ' + lastMedia.toFixed(2) + '->' + mediaT.toFixed(2) + ' seamGap' + Math.round(wallGap) + 'ms');
                if (wallGap >= FRAME_GAP) Dbg.anomaly(label, 'seam', '!SEAM' + Math.round(wallGap));
              } else if (wallGap >= FRAME_GAP) {               // frames stopped painting mid-clip
                Dbg.bump(label, 'fz');
                // jump = how far the clock moved during the gap. ~= wallGap -> clock kept
                // running (pure display/present stall). ~0 -> decode paused too.
                Dbg.log(label, 'HITCH ' + Math.round(wallGap) + 'ms @t' + mediaT.toFixed(2) + ' jump' + Math.round(mediaGap * 1000) + 'ms');
                Dbg.anomaly(label, 'hitch', '!HITCH' + Math.round(wallGap) + '@' + mediaT.toFixed(1));
              }
            }
            seen = true; lastWall = nowW; lastMedia = mediaT;
            if (liveOn) { liveOn = false; Dbg.anomaly(label, 'live', ''); }   // recovered
          } catch (e) {}
          if (tok === self.token) { try { self._rvfcId = v.requestVideoFrameCallback(onFrame); } catch (e) {} }
        };
        try { self._rvfcId = v.requestVideoFrameCallback(onFrame); } catch (e) {}
        // Live watchdog: requestAnimationFrame keeps ticking even while the VIDEO is
        // frozen, so an in-progress freeze (or one that never recovers) is shown live.
        // This is what a screenshot taken DURING the shift will capture.
        (function watch() {
          if (tok !== self.token) { if (liveOn) Dbg.anomaly(label, 'live', ''); return; }
          try {
            if (self.active && !v.paused && !v.seeking && !v.ended) {
              var gap = performance.now() - lastWall;
              if (gap >= FRAME_GAP) { liveOn = true; Dbg.anomaly(label, 'live', '!FROZEN' + Math.round(gap) + 'ms'); }
              else if (liveOn) { liveOn = false; Dbg.anomaly(label, 'live', ''); }
            }
          } catch (e) {}
          requestAnimationFrame(watch);
        })();
      } else {
        // Fallback for very old devices without rVFC: a clock-stall watcher. It CANNOT
        // see the advancing-clock symptom, so it is clearly labelled CLOCKSTALL.
        var lastT = -1, frozenSince = 0, pend = false;
        (function loop() {
          if (tok !== self.token) return;
          var now = performance.now();
          if (self.active && !v.paused && !v.seeking && !v.ended && v.readyState >= 2) {
            if (v.currentTime === lastT) {
              if (!frozenSince) frozenSince = now;
              else if (now - frozenSince >= FRAME_GAP && !pend) {
                pend = true;                        // report the still-ongoing stall live
                Dbg.bump(label, 'fz');
                Dbg.anomaly(label, 'live', '!CLKSTALL' + Math.round(now - frozenSince) + 'ms+');
              }
            } else {
              if (frozenSince) {
                var gap = now - frozenSince;
                if (gap >= FRAME_GAP) { Dbg.log(label, 'CLOCKSTALL ' + Math.round(gap) + 'ms @t' + v.currentTime.toFixed(2)); }
                if (pend) Dbg.anomaly(label, 'live', '');
                frozenSince = 0; pend = false;
              }
              lastT = v.currentTime;
            }
          } else {
            if (pend) { Dbg.anomaly(label, 'live', ''); pend = false; }
            frozenSince = 0;
          }
          requestAnimationFrame(loop);
        })();
      }
    } catch (e) { /* diagnostics must never break playback */ }
  };
  Tile.prototype.hold = function () { this.teardown(); };
  Tile.prototype.teardown = function () {
    var v = this.video;
    if (this.hls) { try { this.hls.stopLoad(); this.hls.destroy(); } catch (e) {} this.hls = null; }
    try { v.pause(); v.removeAttribute('src'); v.load(); } catch (e) {}
    // Ping-pong: stop the buffer partner too, else it keeps fetching/decoding off-screen.
    if (this.videoB) { try { this.videoB.pause(); this.videoB.removeAttribute('src'); this.videoB.load(); } catch (e) {} }
  };
  Tile.prototype.deactivate = function () {
    if (!this.active) return;
    this.active = false;
    this.token++;                                 // invalidate any pending async
    clearTimeout(this.deadline);
    // Cancel a pending debug frame callback so they cannot accumulate across
    // repeated activations (no-op unless ?svdebug=1 ever set _rvfcId).
    if (this._rvfcId != null && this.video && this.video.cancelVideoFrameCallback) {
      try { this.video.cancelVideoFrameCallback(this._rvfcId); } catch (e) {}
      this._rvfcId = null;
    }
    this.revealed = false;
    this.el.classList.remove('is-ready');
    // Reset ping-pong so a re-activation starts cleanly on the primary clip.
    // The token++ above already invalidates any in-flight swap callback; clear the
    // recycle timer too so it cannot pause/reset an element after re-activation.
    if (this._ppTimer) { clearTimeout(this._ppTimer); this._ppTimer = null; }
    this._swapping = false;
    this.front = null; this.back = null;
    if (this.video) this.video.classList.remove('is-front');
    if (this.videoB) this.videoB.classList.remove('is-front');
    this.teardown();
  };

  // ---- viewport gating ----
  var io = null;
  function observer() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      var touched = Object.create(null);
      entries.forEach(function (e) {
        var t = e.target.__ptTile;
        if (!t) return;
        t.ratio = e.isIntersecting ? e.intersectionRatio : 0;
        touched[t.group] = true;
      });
      Object.keys(touched).forEach(scheduleEval);
    }, { threshold: [0, 0.15, 0.35, 0.55, 0.75, 1], rootMargin: '150px 0px' });
    return io;
  }

  var pending = Object.create(null), rafId = 0;
  function scheduleEval(name) {
    pending[name] = true;
    if (rafId) return;
    rafId = requestAnimationFrame(function () {
      rafId = 0;
      var names = Object.keys(pending); pending = Object.create(null);
      names.forEach(evaluateGroup);
    });
  }
  function evaluateGroup(name) {
    var g = groups[name];
    if (!g) return;
    var budget = mqDesktop.matches ? g.tiles.length : 1;
    var cands = g.tiles.filter(function (t) { return t.ratio > 0; })
      .sort(function (a, b) { return (b.ratio - a.ratio) || (a.order - b.order); });
    var winners = cands.slice(0, budget);
    g.tiles.forEach(function (t) {
      if (winners.indexOf(t) >= 0) t.activate(); else t.deactivate();
    });
    if (!mqDesktop.matches) enforceMobileCap();
  }

  // A per-group winner is still only a candidate on mobile: at most ONE tile across
  // THE WHOLE PAGE may be active at a time. Keep whichever active tile currently has
  // the highest intersection ratio (most in-view); deactivate every other active tile,
  // regardless of which group it belongs to. Ties break by DOM order (earlier wins).
  function enforceMobileCap() {
    var active = [];
    Object.keys(groups).forEach(function (name) {
      groups[name].tiles.forEach(function (t) { if (t.active) active.push(t); });
    });
    if (active.length <= 1) return;
    active.sort(function (a, b) { return (b.ratio - a.ratio) || (a.order - b.order); });
    active.slice(1).forEach(function (t) { t.deactivate(); });
  }

  // ---- init / lifecycle ----
  function initScope(root) {
    if (!eligible()) return;                       // poster-only: never observe, never load
    var els = (root || document).querySelectorAll('[data-smart-video]');
    var touched = Object.create(null);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.__ptTile) continue;
      var t = new Tile(el);
      el.__ptTile = t;
      var g = getGroup(t.group);
      t.order = globalTileCount++;
      g.tiles.push(t);
      touched[t.group] = true;
      if (t.video) observer().observe(el);
    }
    Object.keys(touched).forEach(scheduleEval);
  }
  function destroyScope(root) {
    var els = (root || document).querySelectorAll('[data-smart-video]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i], t = el.__ptTile;
      if (!t) continue;
      t.deactivate();
      if (io) io.unobserve(el);
      var g = groups[t.group];
      if (g) { var k = g.tiles.indexOf(t); if (k >= 0) g.tiles.splice(k, 1); g.tiles.forEach(function (x, j) { x.order = j; }); }
      el.__ptTile = null;
    }
  }

  // Re-evaluate on breakpoint change (mobile<->desktop changes the budget).
  function onBP() { Object.keys(groups).forEach(scheduleEval); }
  if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', onBP);
  else if (mqDesktop.addListener) mqDesktop.addListener(onBP);

  // Pause loads when the tab is hidden; re-evaluate when it returns. On return we
  // force each tile to re-observe (unobserve+observe) instead of trusting the ratio
  // it had cached before hiding: that cached value can be stale/wrong (e.g. a mobile
  // app-switch/lock right after load), and nothing else ever re-checks a tile's real
  // position, so a stuck ratio:0 tile would otherwise stay on the poster forever.
  // Re-observing makes the browser hand back a fresh, correct reading immediately.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      Object.keys(groups).forEach(function (n) { groups[n].tiles.forEach(function (t) { t.deactivate(); }); });
    } else {
      Object.keys(groups).forEach(function (n) {
        groups[n].tiles.forEach(function (t) {
          if (t.video && io) { try { io.unobserve(t.el); io.observe(t.el); } catch (e) {} }
        });
      });
      Object.keys(groups).forEach(scheduleEval);
    }
  });

  // Shopify theme editor: sections mount/unmount without a page reload.
  document.addEventListener('shopify:section:load', function (e) { initScope(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyScope(e.target); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initScope(document); });
  } else {
    initScope(document);
  }
})();
